import type { CustomerRevenueReportItem, WaterfallTotalsBreakdown } from '@vy/types';
import { MONTHS } from '@vy/types';
import { entries, isNullOrUndefined, isNotNullOrUndefined, purge } from '@vy/utils';

export type WaterfallTotals = Record<string, Record<string, WaterfallTotalsBreakdown>>;

export function computeWaterfallTotals(reportItems: CustomerRevenueReportItem[]): WaterfallTotals {
  const sortedHeaders = getAllDateHeaders(reportItems);
  const deferred = aggregateDeferredWaterfall(reportItems, sortedHeaders);
  const unbilled = aggregateUnbilledWaterfall(reportItems, sortedHeaders);

  return mergeWaterfalls(deferred, unbilled);
}

function emptyBreakdown(): WaterfallTotalsBreakdown {
  return {
    newDeferredRev: 0,
    recognizedRevenueDeferred: 0,
    endingDeferredBalance: 0,
    newUnbilledAR: 0,
    recognizedRevenueUnbilled: 0,
    endingUnbilledAR: 0,
  };
}

function ensureMonth(result: WaterfallTotals, yearKey: string, month: string) {
  if (!result[yearKey]) {
    result[yearKey] = {};
  }
  if (!result[yearKey][month]) {
    result[yearKey][month] = emptyBreakdown();
  }
}

function mergeWaterfalls(deferred: WaterfallTotals, unbilled: WaterfallTotals): WaterfallTotals {
  const result: WaterfallTotals = {};
  const allYears = new Set([...Object.keys(deferred), ...Object.keys(unbilled)]);

  for (const year of allYears) {
    result[year] = {};
    const allMonths = new Set([
      ...Object.keys(deferred[year] ?? {}),
      ...Object.keys(unbilled[year] ?? {}),
    ]);

    for (const month of allMonths) {
      const deferredBreakdown = deferred[year]?.[month] ?? emptyBreakdown();
      const unbilledBreakdown = unbilled[year]?.[month] ?? emptyBreakdown();

      result[year][month] = {
        newDeferredRev: deferredBreakdown.newDeferredRev,
        recognizedRevenueDeferred: deferredBreakdown.recognizedRevenueDeferred,
        endingDeferredBalance: deferredBreakdown.endingDeferredBalance,
        newUnbilledAR: unbilledBreakdown.newUnbilledAR,
        recognizedRevenueUnbilled: unbilledBreakdown.recognizedRevenueUnbilled,
        endingUnbilledAR: unbilledBreakdown.endingUnbilledAR,
      };
    }
  }

  return result;
}

function getAllDateHeaders(reportItems: CustomerRevenueReportItem[]): string[] {
  const dateStrings = reportItems.flatMap((item) =>
    entries(item.yearlyRevenue).flatMap(([year, revenuePerMonth]) =>
      purge(
        entries(revenuePerMonth).map(([monthKey, breakdown]) =>
          isNullOrUndefined(breakdown) ? undefined : `${monthKey} ${year}`,
        ),
      ),
    ),
  );

  return [...new Set(dateStrings)].sort((a, b) => {
    const aDate = parseDateHeaderForSort(a);
    const bDate = parseDateHeaderForSort(b);
    return aDate.getTime() - bDate.getTime();
  });
}

function aggregateDeferredWaterfall(
  reportItems: CustomerRevenueReportItem[],
  sortedHeaders: string[],
): WaterfallTotals {
  const result: WaterfallTotals = {};

  for (const item of reportItems) {
    for (const [year, revenuePerMonth] of entries(item.yearlyRevenue)) {
      const yearKey = String(year);

      for (const [month, breakdown] of entries(revenuePerMonth)) {
        if (!isNotNullOrUndefined(breakdown)) {
          continue;
        }

        ensureMonth(result, yearKey, month);

        const billed = breakdown.scheduledCharge ?? 0;
        const revenue = breakdown.revenue ?? 0;

        result[yearKey][month].newDeferredRev += billed > revenue ? billed : 0;
        result[yearKey][month].endingDeferredBalance += breakdown.deferredRevenue ?? 0;

        if (billed === revenue) {
          continue;
        }

        if (billed > revenue) {
          result[yearKey][month].recognizedRevenueDeferred += -revenue;
        } else {
          const dateHeader = `${month} ${yearKey}`;
          const headerIndex = sortedHeaders.indexOf(dateHeader);
          const prevHeader = headerIndex > 0 ? sortedHeaders[headerIndex - 1] : null;

          const currentEnding = breakdown.deferredRevenue ?? 0;
          let prevEnding = 0;

          if (isNotNullOrUndefined(prevHeader)) {
            const prevParts = prevHeader.split(' ');
            const prevMonth = prevParts.slice(0, -1).join(' ');
            const prevYear = prevParts[prevParts.length - 1];
            const prevBreakdown = item.yearlyRevenue[prevYear]?.[prevMonth as keyof typeof revenuePerMonth];
            prevEnding = prevBreakdown?.deferredRevenue ?? 0;
          }

          result[yearKey][month].recognizedRevenueDeferred += currentEnding - prevEnding;
        }
      }
    }
  }

  return result;
}

function aggregateUnbilledWaterfall(
  reportItems: CustomerRevenueReportItem[],
  sortedHeaders: string[],
): WaterfallTotals {
  const result: WaterfallTotals = {};

  for (const item of reportItems) {
    let prevEndingUnbilled = 0;
    let hasStarted = false;

    for (const header of sortedHeaders) {
      const parts = header.split(' ');
      const yearKey = parts[parts.length - 1];
      const month = parts.slice(0, -1).join(' ');

      const breakdown = item.yearlyRevenue[yearKey]?.[month as keyof (typeof item.yearlyRevenue)[string]];
      const currentEndingUnbilled = breakdown?.unbilledRevenue ?? (hasStarted ? prevEndingUnbilled : 0);

      if (!hasStarted && !isNotNullOrUndefined(breakdown)) {
        continue;
      }
      hasStarted = true;

      ensureMonth(result, yearKey, month);

      result[yearKey][month].endingUnbilledAR += currentEndingUnbilled;

      const unbilledChange = currentEndingUnbilled - prevEndingUnbilled;
      result[yearKey][month].newUnbilledAR += Math.max(unbilledChange, 0);

      const billed = breakdown?.scheduledCharge ?? 0;
      const revenue = breakdown?.revenue ?? 0;
      const hasUnbilledActivity = currentEndingUnbilled > 0 || prevEndingUnbilled > 0;

      if (billed !== revenue && hasUnbilledActivity) {
        if (revenue > billed) {
          result[yearKey][month].recognizedRevenueUnbilled += revenue;
        } else {
          result[yearKey][month].recognizedRevenueUnbilled += currentEndingUnbilled - prevEndingUnbilled;
        }
      }

      prevEndingUnbilled = currentEndingUnbilled;
    }
  }

  return result;
}

function parseDateHeaderForSort(dateHeader: string): Date {
  const separatorIndex = dateHeader.lastIndexOf(' ');
  const month = dateHeader.slice(0, separatorIndex);
  const year = dateHeader.slice(separatorIndex + 1);
  const monthIndex = MONTHS.indexOf(month as (typeof MONTHS)[number]);
  return new Date(Number(year), monthIndex === -1 ? 0 : monthIndex, 1);
}
